import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  Star,
  Trash2,
  Wand2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CopyButton } from '@/components/ui/copy-button';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/features/auth/auth.store';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { DrawerShell, Empty, ListSkeleton } from './item-drawers';
import { toPriceEntry, type BusinessItemPrice } from './item-master.api';
import {
  useAllModifiers,
  useAssignModifier,
  useAssignedModifiers,
  useBusinessItemLogs,
  useBusinessItemUpcs,
  useCreateBusinessItemUpc,
  useDeleteBusinessItemUpc,
  useResolvePrimaryUpc,
  useSaveItemPrice,
  useSetPrimaryBusinessItemUpc,
  useUnassignModifier,
  useUpdateBusinessItemUpc,
  type BusinessItemLog,
  type ModifierKind,
} from './business-item.api';
import {
  diffPayloads,
  formatPayloadValue,
  parseLogPayload,
  type PayloadValue,
} from './log-payload';

/**
 * The drawers hanging off one store's offer.
 *
 * Barcodes, one price out of several, the modifiers that come with the item at
 * that store, and what has been done to it. They share the item drawers' shell
 * so both halves of Item Master look like one screen.
 */

// ---------------------------------------------------------------- barcodes

export function BusinessItemUpcsSheet({
  businessItemId,
  open,
  onOpenChange,
}: {
  businessItemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const upcs = useBusinessItemUpcs(open ? businessItemId : null);
  const create = useCreateBusinessItemUpc(businessItemId);
  const update = useUpdateBusinessItemUpc(businessItemId);
  const remove = useDeleteBusinessItemUpc(businessItemId);
  const setPrimary = useSetPrimaryBusinessItemUpc(businessItemId);
  const resolvePrimary = useResolvePrimaryUpc(businessItemId);

  const [fresh, setFresh] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const rows = upcs.data ?? [];
  const failed = () => toast.error(t('itemMaster.actionFailed'));

  return (
    <DrawerShell
      title={t('businessItem.upcs')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const value = fresh.trim();
          if (!value) return;
          if (rows.some((row) => row.upc === value)) {
            toast.error(t('itemMaster.gtinAlreadyListed'));
            return;
          }
          create.mutate(value, {
            onSuccess: () => setFresh(''),
            onError: failed,
          });
        }}
      >
        <Input
          value={fresh}
          inputMode="numeric"
          placeholder={t('businessItem.newUpc')}
          onChange={(event) => setFresh(event.target.value)}
        />
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
        </Button>
      </form>

      {/* The store can work its own primary out; the original called this
          "get primary" and so does the endpoint. */}
      <Button
        variant="outline"
        className="w-full"
        disabled={resolvePrimary.isPending}
        onClick={() =>
          resolvePrimary.mutate(undefined, {
            onSuccess: () => toast.success(t('itemMaster.saved')),
            onError: failed,
          })
        }
      >
        {resolvePrimary.isPending && <Loader2 className="animate-spin" />}
        <Wand2 className="size-4" />
        {t('businessItem.resolvePrimary')}
      </Button>

      {upcs.isPending && <ListSkeleton />}
      {upcs.isSuccess && rows.length === 0 && (
        <Empty>{t('businessItem.noUpcs')}</Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={upcs.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              upcs.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div key={row.id} className="flex items-center gap-2 p-2.5">
                {editing === row.id ? (
                  <>
                    <Input
                      value={draft}
                      inputMode="numeric"
                      onChange={(event) => setDraft(event.target.value)}
                    />
                    <Button
                      size="icon-sm"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate(
                          { upcId: row.id, upc: draft },
                          {
                            onSuccess: () => setEditing(null),
                            onError: failed,
                          }
                        )
                      }
                    >
                      <Check className="size-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate font-mono text-sm">
                      {row.upc}
                    </span>
                    {row.isPrimary && (
                      <Badge variant="success">{t('itemMaster.primary')}</Badge>
                    )}
                    {!row.isPrimary && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('itemMaster.makePrimary')}
                        onClick={() =>
                          setPrimary.mutate(row.id, { onError: failed })
                        }
                      >
                        <Star className="size-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('itemMaster.edit')}
                      onClick={() => {
                        setEditing(row.id);
                        setDraft(row.upc ?? '');
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    {/* Deleting the primary would leave the store with none. */}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('itemMaster.remove')}
                      disabled={row.isPrimary}
                      onClick={() =>
                        remove.mutate(row.id, { onError: failed })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

// ------------------------------------------------------------------ a price

const PRICE_METHODS = [
  { value: 0, labelKey: 'businessItem.unit' },
  { value: 1, labelKey: 'businessItem.split' },
];

const TAX_RULES = [
  { value: 0, labelKey: 'businessItem.taxNone' },
  { value: 1, labelKey: 'businessItem.taxMunicipal' },
  { value: 2, labelKey: 'businessItem.taxState' },
  { value: 3, labelKey: 'businessItem.taxBoth' },
];

/**
 * One price out of a store's several.
 *
 * Only reachable for a client that keeps more than one price per item; the
 * single-price case is edited inline on the offer itself. There is no delete,
 * so archiving is an update that clears `isActive` and sets `isDeleted`.
 */
export function ItemPriceSheet({
  businessItemId,
  siblings,
  price,
  open,
  onOpenChange,
}: {
  businessItemId: string | null;
  /** The store's other prices, to keep a new one from colliding with them. */
  siblings: BusinessItemPrice[];
  price: BusinessItemPrice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveItemPrice(businessItemId);

  const [draft, setDraft] = useState<BusinessItemPrice | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);

  const key = price ? price.id || 'new' : null;
  if (price && hydrated !== key) {
    setHydrated(key);
    setDraft(price);
  }

  if (!draft || !price) return null;

  const set = (patch: Partial<BusinessItemPrice>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const failed = () => toast.error(t('itemMaster.actionFailed'));
  const isNew = !draft.id;

  /**
   * A new row has no id, so the server falls back to matching it by name and
   * then by ordering — which would quietly overwrite a sibling that already
   * uses either. Both are known here, so the collision is caught first.
   */
  const others = siblings.filter((row) => row.id !== draft.id && !row.isDeleted);
  const nameTaken = others.some(
    (row) =>
      (row.name ?? '').trim().toLowerCase()
      === (draft.name ?? '').trim().toLowerCase()
  );
  const orderTaken = others.some((row) => row.ordering === draft.ordering);
  const collides = isNew && (nameTaken || orderTaken);

  function submit(archived: boolean) {
    save.mutate(
      {
        ...toPriceEntry(draft!),
        isActive: archived ? false : draft!.isActive,
        isDeleted: archived,
      },
      {
        onSuccess: () => {
          toast.success(t('itemMaster.saved'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <DrawerShell
      title={isNew ? t('businessItem.addPrice') : t('businessItem.editPrice')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
        <span className="text-sm">{t('businessItem.active')}</span>
        <Switch
          checked={draft.isActive}
          onCheckedChange={(isActive) => set({ isActive })}
        />
      </label>

      <div className="space-y-1.5">
        <Label>{t('itemMaster.name')}</Label>
        <Input
          value={draft.name ?? ''}
          onChange={(event) => set({ name: event.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>{t('itemMaster.description')}</Label>
        <Input
          value={draft.description ?? ''}
          onChange={(event) => set({ description: event.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label={t('businessItem.ordering')}
          value={draft.ordering}
          onChange={(ordering) => set({ ordering })}
        />
        <NumberField
          label={t('businessItem.pack')}
          value={draft.pack}
          min={1}
          onChange={(pack) => set({ pack })}
        />
        <NumberField
          label={t('businessItem.price')}
          value={draft.basePrice}
          step="0.01"
          onChange={(basePrice) => set({ basePrice })}
        />
        <NumberField
          label={t('businessItem.multiple')}
          value={draft.baseMultiple}
          min={1}
          onChange={(baseMultiple) => set({ baseMultiple })}
        />
        <NumberField
          label={t('businessItem.cost')}
          value={draft.cost}
          step="0.01"
          onChange={(cost) => set({ cost })}
        />
        <NumberField
          label={t('businessItem.costOfBox')}
          value={draft.costOfBox}
          step="0.01"
          onChange={(costOfBox) => set({ costOfBox })}
        />
      </div>

      <EnumField
        label={t('businessItem.priceMethod')}
        value={draft.basePriceMethod}
        options={PRICE_METHODS}
        onChange={(basePriceMethod) => set({ basePriceMethod })}
      />
      <EnumField
        label={t('businessItem.taxRule')}
        value={draft.taxRule}
        options={TAX_RULES}
        onChange={(taxRule) => set({ taxRule })}
      />

      <p className="pt-2 text-sm font-medium">
        {t('businessItem.promoPrice')}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label={t('businessItem.price')}
          value={draft.promoPrice}
          step="0.01"
          onChange={(promoPrice) => set({ promoPrice })}
        />
        <NumberField
          label={t('businessItem.multiple')}
          value={draft.promoMultiple}
          min={1}
          onChange={(promoMultiple) => set({ promoMultiple })}
        />
      </div>
      <EnumField
        label={t('businessItem.priceMethod')}
        value={draft.promoPriceMethod}
        options={PRICE_METHODS}
        onChange={(promoPriceMethod) => set({ promoPriceMethod })}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>{t('businessItem.startDate')}</Label>
          <Input
            type="datetime-local"
            value={(draft.promoStartDate ?? '').slice(0, 16).replace(' ', 'T')}
            onChange={(event) =>
              set({
                promoStartDate: event.target.value
                  ? `${event.target.value}:00.000`
                  : null,
              })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t('businessItem.endDate')}</Label>
          <Input
            type="datetime-local"
            value={(draft.promoEndDate ?? '').slice(0, 16).replace(' ', 'T')}
            onChange={(event) =>
              set({
                promoEndDate: event.target.value
                  ? `${event.target.value}:00.000`
                  : null,
              })
            }
          />
        </div>
      </div>

      {collides && (
        <p className="text-destructive text-sm">
          {nameTaken
            ? t('businessItem.priceNameTaken')
            : t('businessItem.priceOrderTaken')}
        </p>
      )}

      <div className="flex justify-between gap-2 pt-2">
        {!isNew ? (
          <Button
            variant="destructive"
            disabled={save.isPending}
            onClick={() => submit(true)}
          >
            {t('businessItem.removePrice')}
          </Button>
        ) : (
          <span />
        )}
        <Button
          disabled={save.isPending || collides}
          onClick={() => submit(false)}
        >
          {save.isPending && <Loader2 className="animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </DrawerShell>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step,
  min,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (value: number) => void;
  step?: string;
  min?: number;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        inputMode="decimal"
        step={step}
        min={min}
        value={value ?? 0}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

function EnumField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: number;
  options: { value: number; labelKey: string }[];
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select
        value={String(value)}
        onValueChange={(next) => onChange(Number(next))}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {t(option.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// --------------------------------------------- requirements, sides, add-ons

const MODIFIER_TABS: { kind: ModifierKind; labelKey: string }[] = [
  { kind: 'requirement', labelKey: 'businessItem.requirements' },
  { kind: 'side', labelKey: 'businessItem.sides' },
  { kind: 'addon', labelKey: 'businessItem.addons' },
];

/**
 * What comes with the item: its requirements, its sides, its add-ons.
 *
 * Where the assignment lives depends on the client. When modifiers are kept
 * globally the item carries them and every store inherits; otherwise each
 * store keeps its own, and both the list and the assign call change shape.
 */
export function ModifiersSheet({
  businessItemId,
  businessId,
  itemId,
  open,
  onOpenChange,
}: {
  businessItemId: string;
  businessId: string | null;
  itemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const global = useAuthStore((state) => !!state.state?.globalReqSideAddons);

  const scope = global
    ? { businessItemId: null, itemId, businessId: null }
    : { businessItemId, itemId, businessId };

  return (
    <DrawerShell
      title={t('businessItem.modifiers')}
      open={open}
      onOpenChange={onOpenChange}
    >
      <p className="text-muted-foreground text-xs">
        {global
          ? t('businessItem.modifiersGlobal')
          : t('businessItem.modifiersPerStore')}
      </p>

      <Tabs defaultValue="requirement">
        <TabsList className="w-full">
          {MODIFIER_TABS.map((tab) => (
            <TabsTrigger key={tab.kind} value={tab.kind} className="flex-1">
              {t(tab.labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
        {MODIFIER_TABS.map((tab) => (
          <TabsContent key={tab.kind} value={tab.kind}>
            <ModifierList
              kind={tab.kind}
              scope={scope}
              itemId={itemId}
              businessItemId={businessItemId}
              enabled={open}
            />
          </TabsContent>
        ))}
      </Tabs>
    </DrawerShell>
  );
}

function ModifierList({
  kind,
  scope,
  itemId,
  businessItemId,
  enabled,
}: {
  kind: ModifierKind;
  scope: { businessItemId: string | null; itemId: string | null; businessId: string | null };
  itemId: string | null;
  businessItemId: string;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  // One owner or the other, never both: the server answers for whichever id
  // is filled, and sending the pair would let the wrong one win.
  const assigned = useAssignedModifiers(kind, {
    itemId: scope.businessItemId ? null : itemId,
    businessItemId: scope.businessItemId ? businessItemId : null,
    enabled,
  });
  const all = useAllModifiers(kind, { businessId: scope.businessId, enabled });
  const assign = useAssignModifier(kind, scope);
  const unassign = useUnassignModifier(kind, scope);

  const [term, setTerm] = useState('');
  const failed = () => toast.error(t('itemMaster.actionFailed'));

  const assignedIds = new Set((assigned.data ?? []).map((row) => row.id));
  const available = (all.data ?? [])
    .filter((row) => !assignedIds.has(row.id))
    .filter((row) =>
      term.trim() === ''
        ? true
        : `${row.name ?? ''} ${row.internalName ?? ''}`
            .toLowerCase()
            .includes(term.trim().toLowerCase())
    );

  return (
    <div className="space-y-3">
      {(assigned.isPending || all.isPending) && <ListSkeleton />}

      {assigned.isSuccess && (assigned.data ?? []).length === 0 && (
        <Empty>{t('businessItem.noneAssigned')}</Empty>
      )}

      {(assigned.data ?? []).length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={assigned.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              assigned.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {(assigned.data ?? []).map((row) => (
              <div key={row.id} className="flex items-center gap-2 p-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.name}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.internalName}
                  </p>
                </div>
                <Badge variant="secondary">
                  {row.minRequired}–{row.maxRequired}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('itemMaster.remove')}
                  disabled={unassign.isPending}
                  onClick={() => unassign.mutate(row.id, { onError: failed })}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Input
        value={term}
        placeholder={t('businessItem.searchModifiers')}
        onChange={(event) => setTerm(event.target.value)}
      />

      {all.isSuccess && available.length === 0 && (
        <Empty>{t('businessItem.nothingToAssign')}</Empty>
      )}

      {available.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={all.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              all.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {available.map((row) => (
              <button
                key={row.id}
                type="button"
                disabled={assign.isPending}
                onClick={() => assign.mutate(row.id, { onError: failed })}
                className="hover:bg-accent flex w-full items-center gap-2 p-2.5 text-left transition-colors disabled:opacity-50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.name}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.internalName}
                  </p>
                </div>
                <Plus className="size-4 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------- logs

export function BusinessItemLogsSheet({
  businessItemId,
  open,
  onOpenChange,
}: {
  businessItemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const size = 20;
  const logs = useBusinessItemLogs(open ? businessItemId : null, page, size);

  const rows = logs.data?.logs ?? [];

  return (
    <DrawerShell
      title={t('itemMaster.logs')}
      open={open}
      onOpenChange={onOpenChange}
      width="sm:max-w-2xl"
    >
      {logs.isPending && <ListSkeleton />}
      {logs.isError && <Empty>{t('itemMaster.logsFailed')}</Empty>}
      {logs.isSuccess && rows.length === 0 && (
        <Empty>{t('itemMaster.noLogs')}</Empty>
      )}

      {rows.length > 0 && (
        <>
          <div className="relative rounded-xl">
            <BusyOverlay show={logs.isFetching} />
            <div
              className={cn(
                'divide-y rounded-xl border',
                logs.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((log, index) => (
                <LogEntry
                  key={log.id ?? index}
                  log={log}
                  // Logs come back newest first, so the entry that preceded this
                  // one is the next in the list. On the last row of a page there
                  // is nothing loaded to compare against, and the row says so.
                  previous={rows[index + 1]}
                />
              ))}
            </div>
          </div>
          <Pagination
            page={page}
            size={size}
            total={logs.data?.count ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </DrawerShell>
  );
}

type LogView = 'changes' | 'all' | 'raw';

/**
 * One log entry, with its payload openable underneath.
 *
 * The `data` blob is the whole record the POS or the admin tools sent, which
 * for a POS sync is ~60 fields of which two or three are the point. So the
 * default view is what this entry *changed* against the entry before it; the
 * full field list and the raw JSON are a click away for when that is not
 * enough.
 */
function LogEntry({
  log,
  previous,
}: {
  log: BusinessItemLog;
  previous?: BusinessItemLog;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<LogView | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const payload = useMemo(() => parseLogPayload(log.data), [log.data]);
  const earlier = useMemo(
    () => parseLogPayload(previous?.data),
    [previous?.data]
  );
  const changes = useMemo(
    () => (payload && earlier ? diffPayloads(payload, earlier) : null),
    [payload, earlier]
  );

  const locale = i18n.resolvedLanguage === 'es' ? 'es-PR' : 'en-US';
  const words = {
    yes: t('common.yes'),
    no: t('common.no'),
    empty: '\u2014',
  };
  const show = (value: PayloadValue) =>
    formatPayloadValue(value, locale, words);

  const active: LogView = view ?? (changes ? 'changes' : 'all');
  const emptyCount = payload
    ? payload.sections.reduce(
        (total, section) =>
          total + section.fields.filter((f) => f.value.kind === 'empty').length,
        0
      )
    : 0;

  const tabs: { id: LogView; label: string }[] = [
    { id: 'changes', label: t('itemMaster.logChanges') },
    { id: 'all', label: t('itemMaster.logAllFields') },
    { id: 'raw', label: t('itemMaster.logRaw') },
  ];

  return (
    <div className="p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <p className="text-muted-foreground text-xs">
            {formatDateTime(log.createdAt)}
            {log.location && (
              <span className="ml-1.5 font-mono opacity-70">
                {log.location}
              </span>
            )}
          </p>
          <p className="text-sm text-pretty">{log.message}</p>
        </div>

        {payload && (
          <button
            type="button"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            aria-expanded={open}
            className={cn(
              'text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1',
              'rounded px-1.5 py-0.5 text-xs transition-colors',
              'focus-visible:ring-ring/50 outline-none focus-visible:ring-2'
            )}
          >
            {t('itemMaster.logDetails')}
            <ChevronDown
              className={cn(
                'size-3.5 transition-transform',
                open && 'rotate-180'
              )}
            />
          </button>
        )}
      </div>

      {open && payload && (
        <div className="mt-3 space-y-3">
          <div className="bg-muted inline-flex rounded-lg p-0.5">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setView(tab.id)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs transition-colors',
                  'focus-visible:ring-ring/50 outline-none focus-visible:ring-2',
                  active === tab.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {active === 'changes' && (
            <div className="rounded-lg border">
              {!changes ? (
                <p className="text-muted-foreground p-3 text-xs">
                  {t('itemMaster.logNoPrevious')}
                </p>
              ) : changes.length === 0 ? (
                <p className="text-muted-foreground p-3 text-xs">
                  {t('itemMaster.logNoChanges')}
                </p>
              ) : (
                <ul className="divide-y">
                  {changes.map((change) => (
                    <li
                      key={change.key}
                      className="flex flex-wrap items-baseline gap-x-2 gap-y-1 p-2.5"
                    >
                      <span className="text-muted-foreground w-full text-xs sm:w-48 sm:shrink-0">
                        {change.label}
                      </span>
                      <span className="text-muted-foreground text-sm line-through">
                        {show(change.before)}
                      </span>
                      <ArrowRight className="text-muted-foreground size-3 shrink-0" />
                      <span className="text-sm font-medium">
                        {show(change.after)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {active === 'all' && (
            <div className="space-y-3">
              {payload.sections.map((section, sectionIndex) => {
                const fields = showEmpty
                  ? section.fields
                  : section.fields.filter((f) => f.value.kind !== 'empty');
                if (fields.length === 0) return null;

                return (
                  <div
                    key={section.name ?? `section-${sectionIndex}`}
                    className="rounded-lg border"
                  >
                    {section.label && (
                      <p className="text-muted-foreground border-b px-2.5 py-1.5 text-xs font-medium">
                        {section.label}
                      </p>
                    )}
                    <dl className="divide-y">
                      {fields.map((field) => (
                        <div
                          key={field.key}
                          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 p-2.5"
                        >
                          <dt className="text-muted-foreground w-full text-xs sm:w-48 sm:shrink-0">
                            {field.label}
                          </dt>
                          <dd
                            className={cn(
                              'min-w-0 text-sm break-words',
                              field.value.kind === 'empty' &&
                                'text-muted-foreground'
                            )}
                          >
                            {show(field.value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })}

              {emptyCount > 0 && (
                <button
                  type="button"
                  onClick={() => setShowEmpty((was) => !was)}
                  className="text-muted-foreground hover:text-foreground rounded text-xs underline underline-offset-2 focus-visible:ring-ring/50 outline-none focus-visible:ring-2"
                >
                  {showEmpty
                    ? t('itemMaster.logHideEmpty')
                    : t('itemMaster.logShowEmpty', { count: emptyCount })}
                </button>
              )}
            </div>
          )}

          {active === 'raw' && (
            <div className="group bg-muted/40 relative rounded-lg border">
              <CopyButton
                value={payload.pretty}
                label={t('itemMaster.logCopyData')}
                className="absolute top-1.5 right-1.5 z-10"
              />
              <pre className="max-h-80 overflow-auto p-2.5 font-mono text-xs leading-relaxed">
                {payload.pretty}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
